/**
 * Triggers a client-side download of an in-memory text file (the chat
 * transcript). Creates a Blob object-URL and clicks a hidden `<a download>`.
 *
 * Returns `true` when the download was initiated, `false` when the environment
 * refused it (Blob / object-URL unavailable, or a sandboxed context where the
 * APIs throw). The caller can fall back on a `false` — note a sandboxed iframe
 * WITHOUT `allow-downloads` can silently block the click without throwing, so a
 * `true` return means "initiated", not "the browser definitely saved a file".
 */
export function downloadTextFile(
  filename: string,
  text: string,
  mimeType = "text/plain;charset=utf-8",
): boolean {
  try {
    const blob = new Blob([text], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after the click has consumed the URL (immediate revoke can cancel
    // the download in some browsers).
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch {
    return false;
  }
}

/** Slugifies a label into a safe file-name stem (no extension). */
export function safeFilenameStem(label: string, fallback = "transcript"): string {
  const stem = label
    .normalize("NFKD")
    .replace(/[^\w.-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return stem || fallback;
}
